import { Model } from '@vuex-orm/core';
import { logError } from '@/functions/errorhandler';
import Availability from '@/models/Availability';
import Bathroom from '@/models/Bathroom';
import Bedroom from '@/models/Bedroom';
import Sectionable from '@/models/Sectionable';
import Date from '@/models/Date';
import Facility from '@/models/Facility';
import Facts from '@/models/Facts';
import File from '@/models/File';
import Image from '@/models/Image';
import Imageable from '@/models/Imageable';
import Level from '@/models/Level';
import Levelable from '@/models/Levelable';
import Link from '@/models/Link';
import Project from '@/models/Project';
import Projectable from '@/models/Projectable';
import ResidenceType from '@/models/ResidenceType';
import Section from '@/models/Section';
import Shape from '@/models/Shape';
import OptionVariable from '@/models/OptionVariable';
import FacilityResidence from '@/models/FacilityResidence';

export default class Residence extends Model {
  // This is the name used as module name of the Vuex Store.
  static entity = 'residences';

  static state() {
    return {
      loadStatus: 'loading',
      viewed: '',
      hasThumbnails: false,
      hasImages: [],
      selectedProject: false,
      maxPrice: '',
      minPrice: '',
      maxSize: '',
      minSize: '',
      bedrooms: [],
      bathrooms: [],
      residenceStatus: 'all',
      selectedLevel: 'all',
      selectedResidenceType: 'all',
      listType: 'list',
      order: {
        orderBy: 'name',
        sortOrder: 'asc',
      },
      hasContactForm: false,
      colorSection: false,
      colorResidence: false,
    };
  }

  // List of all fields (schema) of the residence model.
  static fields() {
    return {
      id: this.uid(),
      name: this.attr(''),
      slug: this.attr(null),
      link: this.attr(null),
      description: this.attr(''),
      longDescription: this.attr(null),
      hasInterestRate: this.boolean(false),
      interestRate: this.attr(null),
      hasCustomDepositSize: this.boolean(false),
      customDepositSize: this.attr(null),
      hasInstallmentFreePeriod: this.boolean(false),
      installmentFreePeriod: this.attr(null),
      price: this.number(0),
      jointDebt: this.number(0),
      costs: this.number(0),
      size: this.number(null),
      hasImages: this.boolean(true),
      hasImagesLoaded: this.boolean(false),
      airtableId: this.attr(null),

      // Relationships
      availability_id: this.attr(null),
      availability: this.belongsTo(Availability, 'availability_id'),
      bathroom_id: this.attr(null),
      bathrooms: this.belongsTo(Bathroom, 'bathroom_id'),
      bedroom_id: this.attr(null),
      bedrooms: this.belongsTo(Bedroom, 'bedroom_id'),
      sections: this.morphToMany(
        Section,
        Sectionable,
        'section_id',
        'sectionable_id',
        'sectionable_type'
      ),
      date: this.morphOne(Date, 'dateable_id', 'dateable_type'),
      images: this.morphToMany(
        Image,
        Imageable,
        'image_id',
        'imageable_id',
        'imageable_type'
      ),
      facilities: this.belongsToMany(
        Facility,
        FacilityResidence,
        'residence_id',
        'facility_id'
      ),
      facts: this.hasOne(Facts, 'residence_id'),
      files: this.hasMany(File, 'residence_id'),
      levels: this.morphToMany(
        Level,
        Levelable,
        'level_id',
        'levelable_id',
        'levelable_type'
      ),
      links: this.hasMany(Link, 'residence_id'),
      shapes: this.morphMany(Shape, 'shapeable_id', 'shapeable_type'),
      option_variables: this.hasMany(OptionVariable, 'residence_id'),
      projects: this.morphToMany(
        Project,
        Projectable,
        'project_id',
        'projectable_id',
        'projectable_type'
      ),
      residencetype_id: this.attr(null),
      residenceType: this.belongsTo(ResidenceType, 'residencetype_id'),

      // Default placeholders
      monthlyCost: this.number(0),
      hasFacts: this.boolean(false),
      isSelected: this.boolean(false),
      isHidden: this.boolean(false),
      isFavourite: this.boolean(false),
    };
  }

  totalPrice() {
    return this.price + this.jointDebt;
  }

  /* =================================== *\
      Transforms
  \* =================================== */
  static transformReducedData(data) {
    const residences = [];
    data.data.forEach(data => {
      const residence = {};
      residence.id = data.id;
      residence.name = data.title.rendered.replace(/&#8211;/g, '–');
      residence.slug = data.slug;
      residence.link = data.link;
      if (data.acf.price) {
        residence.price = parseFloat(data.acf.price);
      }
      if (data.acf.joint_debt) {
        residence.jointDebt = parseFloat(data.acf.joint_debt);
      }
      residence.size = data.acf.size;

      if (data.availability && data.availability.length > 0) {
        residence.availability = data.availability[0];
        residence.availability_id = data.availability[0];
      }
      if (data.rooms && data.rooms.length > 0) {
        residence.bedrooms = data.rooms[0];
        residence.bedroom_id = data.rooms[0];
      }
      if (data.section && data.section.length > 0) {
        residence.sections = [];
        data.section.forEach(section => {
          residence.sections.push({ id: section });
        });
      }
      if (data.type && data.type.length > 0) {
        residence.residenceType = data.type[0];
        residence.residencetype_id = data.type[0];
      }
      // Check if there are shapes.
      if (data.acf.shapes) {
        // Add shapes to Residence object.
        // Data from WordPress has the correct schema.
        residence.shapes = data.acf.shapes;
      }
      residences.push(residence);
    });
    return residences;
  }

  static transformAllData(data, hasImagesLoaded) {
    const residence = {};
    residence.id = data.id;
    residence.name = data.title.rendered.replace(/&#8211;/g, '–');
    residence.slug = data.slug;
    residence.link = data.link;

    if (data.acf.price) {
      residence.price = parseFloat(data.acf.price);
    }
    if (data.acf.joint_debt) {
      residence.jointDebt = parseFloat(data.acf.joint_debt);
    }
    if (data.acf.costs) {
      residence.costs = parseFloat(data.acf.costs);
    }
    residence.size = data.acf.size;

    residence.description = data.acf.description;
    residence.longDescription = data.acf.long_description;

    residence.facts = {
      floor: data.acf.floor,
      size: parseFloat(data.acf.size),
      floorSize: parseFloat(data.acf.floorsize),
      grossSize: parseFloat(data.acf.grosssize),

      storage: data.acf.storage,
      garden: data.acf.garden,
      lotsize: data.acf.lotsize,
      terrace: data.acf.terrace,
      balcony: data.acf.balcony,
      glazed_balcony: data.acf.glazed_balcony,
      garage: data.acf.garage,
      carport: data.acf.carport,
      parking: data.acf.parking,
    };

    if (data.acf.bedrooms) {
      residence.facts.bedrooms = data.acf.bedrooms.name;
    }

    if (data.acf.bathrooms) {
      residence.facts.bathrooms = data.acf.bathrooms.name;
    }

    /* ================================================= *\
      * WordPress Taxonomies - start
      *
      * Connect the Residence to various taxonomies which
      * have been inserted seperately by referencing ID.
    \* ================================================= */

    if (data.availability && data.availability.length > 0) {
      residence.availability = data.availability[0];
      residence.availability_id = data.availability[0];
    }

    if (data.bathrooms && data.bathrooms.length > 0) {
      residence.bathrooms = data.bathrooms[0];
      residence.bathroom_id = data.bathrooms[0];
    }

    if (data.rooms && data.rooms.length > 0) {
      residence.bedrooms = data.rooms[0];
      residence.bedroom_id = data.rooms[0];
    }

    if (data.section && data.section.length > 0) {
      residence.sections = [];
      data.section.forEach(section => {
        residence.sections.push({ id: section });
      });
    }

    if (data.facility && data.facility.length > 0) {
      residence.facilities = [];
      data.facility.forEach(facilityId => {
        residence.facilities.push({ id: facilityId });
      });
    }

    if (data.level && data.level.length > 0) {
      residence.levels = [];
      data.level.forEach(level => {
        residence.levels.push({ id: level });
      });
    }

    if (data.project && data.project.length > 0) {
      residence.projects = [];
      data.project.forEach(project => {
        residence.projects.push({ id: project });
      });
    }

    if (data.type && data.type.length > 0) {
      residence.residenceType = data.type[0];
      residence.residencetype_id = data.type[0];
    }
    /* ================================================= *\
      * WordPress Taxonomies - end
    \* ================================================= */

    // Add date object to Residence object
    if (residence.date) {
      residence.date = {
        date: data.date,
        date_gmt: data.date_gmt,
        modified: data.modified,
        modified_gmt: data.modified_gmt,
      };
    }

    // Check if there are any images
    if (data.acf.images && hasImagesLoaded === false) {
      // Create empty array on Residence object.
      residence.images = [];
      // Loop through each image, clean up data and add to array.
      data.acf.images.forEach(image => {
        residence.images.push({
          id: image.id,
          filename: image.filename,
          title: image.title,
          alt: image.alt,
          caption: image.caption,
          description: image.description,
          mime_type: image.mime_type,
          type: image.type,
          subtype: image.subtype,
          url: image.url,
          width: image.width,
          height: image.height,
          date: {
            date: image.date,
            modified: image.modified,
          },
          sizes: [
            {
              name: 'thumbnail',
              url: image.sizes.thumbnail,
              width: image.sizes['thumbnail-width'],
              height: image.sizes['thumbnail-height'],
            },
            {
              name: 'medium',
              url: image.sizes.medium,
              width: image.sizes['medium-width'],
              height: image.sizes['medium-height'],
            },
            {
              name: 'medium_large',
              url: image.sizes.medium_large,
              width: image.sizes['medium_large-width'],
              height: image.sizes['medium_large-height'],
            },
            {
              name: 'large',
              url: image.sizes.large,
              width: image.sizes['large-width'],
              height: image.sizes['large-height'],
            },
            {
              name: '1536x1536',
              url: image.sizes['1536x1536'],
              width: image.sizes['1536x1536-width'],
              height: image.sizes['1536x1536-height'],
            },
            {
              name: '2048x2048',
              url: image.sizes['2048x2048'],
              width: image.sizes['2048x2048-width'],
              height: image.sizes['2048x2048-height'],
            },
            {
              name: 'preview_horisontal',
              url: image.sizes['preview_horisontal'],
              width: image.sizes['preview_horisontal-width'],
              height: image.sizes['preview_horisontal-height'],
            },
            {
              name: 'preview_square',
              url: image.sizes['preview_square'],
              width: image.sizes['preview_square-width'],
              height: image.sizes['preview_square-height'],
            },
            {
              name: 'preview_vertical',
              url: image.sizes['preview_vertical'],
              width: image.sizes['preview_vertical-width'],
              height: image.sizes['preview_vertical-height'],
            },
          ],
        });
      });
      residence.hasImagesLoaded = true;
    }

    // Check if there are any files.
    if (data.acf.files && data.acf.files.length > 0) {
      // Create empty array on Residence object.
      residence.files = [];
      // Loop through each file and add to array.
      data.acf.files.forEach(data => {
        const file = data.file;
        if (file != false) {
          residence.files.push(
            // Clean up data to match VUEX ORM schema.
            {
              id: file.id,
              filename: file.filename,
              url: file.url,
              title: file.title,
              alt: file.alt,
              caption: file.caption,
              description: file.description,
              mime_type: file.mime_type,
              type: file.type,
              subtype: file.subtype,
              date: {
                date: file.date,
                modified: file.modified,
              },
            }
          );
        }
      });
    }

    // Check if there are any links
    if (data.acf.links) {
      // Add links to Residence object.
      // Data from WordPress has the correct schema.
      residence.links = data.acf.links;
    }

    // Check if there are shapes.
    if (data.acf.shapes) {
      // Add shapes to Residence object.
      // Data from WordPress has the correct schema.
      residence.shapes = data.acf.shapes;
    }

    // Check if there are option variables
    if (data.acf.option_variables) {
      // Add option variables to Residence object.
      // Data from WordPress has the correct schema.
      residence.option_variables = data.acf.option_variables;
    }
    residence.hasFacts = true;

    residence.hasInterestRate = data.acf.individualInterestRateCheck;
    if (data.acf.individualInterestRateCheck) {
      residence.interestRate = parseFloat(data.acf.individual_interest_rate);
    }
    residence.hasCustomDepositSize = data.acf.deposit_share_check;
    if (data.acf.deposit_share_check) {
      residence.customDepositSize = parseFloat(data.acf.deposit_share);
    }
    residence.hasInstallmentFreePeriod =
      data.acf.individual_interest_free_check;
    if (data.acf.individual_interest_free_check) {
      residence.installmentFreePeriod = parseFloat(
        data.acf.individual_interest_free_years
      );
    }

    if (data.acf.airtable_id) {
      residence.airtableId = data.acf.airtable_id;
    }

    return residence;
  }

  static transformDataWithImages(data, hasImages) {
    const residences = [];
    data.data.forEach(data => {
      const residence = {};
      residence.id = data.id;
      // Check if current residence already has images.
      if (hasImages.includes(residence.id) === false) {
        // Check if there are any images
        if (data.acf.images) {
          // Create empty array on Residence object.
          residence.images = [];
          // Loop through each image, clean up data and add to array.
          data.acf.images.forEach(image => {
            residence.images.push({
              id: image.id,
              filename: image.filename,
              title: image.title,
              alt: image.alt,
              caption: image.caption,
              description: image.description,
              mime_type: image.mime_type,
              type: image.type,
              subtype: image.subtype,
              url: image.url,
              width: image.width,
              height: image.height,
              date: {
                date: image.date,
                modified: image.modified,
              },
              sizes: [
                {
                  name: 'thumbnail',
                  url: image.sizes.thumbnail,
                  width: image.sizes['thumbnail-width'],
                  height: image.sizes['thumbnail-height'],
                },
                {
                  name: 'medium',
                  url: image.sizes.medium,
                  width: image.sizes['medium-width'],
                  height: image.sizes['medium-height'],
                },
                {
                  name: 'medium_large',
                  url: image.sizes.medium_large,
                  width: image.sizes['medium_large-width'],
                  height: image.sizes['medium_large-height'],
                },
                {
                  name: 'large',
                  url: image.sizes.large,
                  width: image.sizes['large-width'],
                  height: image.sizes['large-height'],
                },
                {
                  name: '1536x1536',
                  url: image.sizes['1536x1536'],
                  width: image.sizes['1536x1536-width'],
                  height: image.sizes['1536x1536-height'],
                },
                {
                  name: '2048x2048',
                  url: image.sizes['2048x2048'],
                  width: image.sizes['2048x2048-width'],
                  height: image.sizes['2048x2048-height'],
                },
                {
                  name: 'preview_horisontal',
                  url: image.sizes['preview_horisontal'],
                  width: image.sizes['preview_horisontal-width'],
                  height: image.sizes['preview_horisontal-height'],
                },
                {
                  name: 'preview_square',
                  url: image.sizes['preview_square'],
                  width: image.sizes['preview_square-width'],
                  height: image.sizes['preview_square-height'],
                },
                {
                  name: 'preview_vertical',
                  url: image.sizes['preview_vertical'],
                  width: image.sizes['preview_vertical-width'],
                  height: image.sizes['preview_vertical-height'],
                },
              ],
            });
          });
          residence.hasImagesLoaded = true;
        } else {
          residence.hasImages = false;
        }
      }
      residences.push(residence);
    });
    return residences;
  }

  /* =================================== *\
      Get data
  \* =================================== */
  static fetch(project, baseURL, exclude) {
    //let url = '/wp-json/wp/v2/residence?per_page=100&per_page=100';
    let i = 1;
    const endpoint = '/wp-json/wp/v2/residence';
    const fields =
      '_fields=id,title,slug,link,acf.price,acf.joint_debt,section,availability,type,bathroom,rooms,acf.size,acf.shapes';
    let url = '';
    url = endpoint + '?' + fields;
    if (exclude) {
      url = url + '&availability_exclude=' + exclude;
    }
    if (project) {
      url = url + '&project=' + project;
    }
    url = url + '&per_page=100&page=' + i;

    return this.api()
      .get(url, {
        dataTransformer: data => this.transformReducedData(data),
        baseURL: baseURL,
      })
      .then(function (response) {
        const totalPages = response.response.headers['x-wp-totalpages'];
        if (totalPages > 1) {
          Residence.commit(state => {
            state.loadStatus = 'loaded';
          });
          i = i + 1;
          url = endpoint + '?' + fields;
          if (project) {
            url = url + '&project=' + project;
          }
          url = url + '&per_page=100&page=' + i;
          Residence.api().get(url, {
            dataTransformer: data => Residence.transformReducedData(data),
            baseURL: baseURL,
          });
        } else if (response.response.data.length > 0) {
          Residence.commit(state => {
            state.loadStatus = 'loaded';
          });
        } else {
          Residence.commit(state => {
            state.loadStatus = 'empty';
          });
        }
      })
      .catch(function (error) {
        console.log(error);
        console.log(error.message);
        console.log(error.request);
        console.log(error.response);
        window.__error__ = error;
        Residence.commit(state => {
          state.loadStatus = 'error';
        });
        logError({
          site: window.location.href,
          project: project,
          type: 'residence',
          error: error,
          response: error.response,
          request: error.request.status,
          message: error.message,
        });
      });
  }

  static fetchAllWithImages(project, baseURL, exclude, hasImages) {
    let i = 1;
    const endpoint = '/wp-json/wp/v2/residence';
    const fields = '_fields=id,acf.images';
    let url = '';
    url = endpoint + '?' + fields;
    if (exclude) {
      url = url + '&availability_exclude=' + exclude;
    }
    if (project) {
      url = url + '&project=' + project;
    }
    url = url + '&per_page=100&page=' + i;
    return this.api()
      .get(url, {
        dataTransformer: data => this.transformDataWithImages(data, hasImages),
        baseURL: baseURL,
      })
      .then(response => {
        const totalPages = response.response.headers['x-wp-totalpages'];
        if (totalPages > 1) {
          while (i < totalPages) {
            i = i + 1;
            url = endpoint + '?' + fields;
            if (project) {
              url = url + '&project=' + project;
            }
            url = url + '&per_page=100&page=' + i;
            Residence.api().get(url, {
              dataTransformer: data => Residence.transformDataWithImages(data),
              baseURL: baseURL,
            });
          }
        }
      });
  }

  static getResidence(id, baseURL, hasImagesLoaded) {
    return this.api().get(`/wp-json/wp/v2/residence/${id}`, {
      dataTransformer: data =>
        this.transformAllData(data.data, hasImagesLoaded),
      baseURL: baseURL,
    });
  }

  /* =================================== *\
      Filterkode
  \* =================================== */
  /* Example code from vuex
  static withComments(sort = 'asc') {
    return this.query().with('comments', (query) => {
      query.orderBy('title', sort)
    })
  }

  //TODO: Gjøre ferdig filtermetode.
  static filter(level = 'all', residenceType = 'all') {
    if (level !== 'all') {
      return Residence.query()
        .whereHas('levels', query => {
          query.where('id', level);
        })
        .get();
    }
    if (residenceType !== 'all') {
      return Residence.query()
        .whereHas('residenceType', query => {
          query.where('slug', residenceType);
        })
        .get();
    }
    return Residence.all();
  }*/
}
